import { CountrySelectorCustomRender, FilterOptions } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useHeaderControlsContext } from "~/components/contexts/useHeaderControlsContext"
import { FindProductionSitesFilterOptionsByOrganizationIdResponseDtoActivity } from "~/gen/api"
import { useFindProductionSitesFilterOptionsByOrganizationId } from "~/gen/api/hooks/useFindProductionSitesFilterOptionsByOrganizationId"
import type { FindProductionSitesForBrandsRequestFiltersDto } from "~/gen/api/types/FindProductionSitesForBrandsRequestFiltersDto"
import { useAuth } from "~/store/auth-context"
import { COUNTRIES } from "~/utils/countries"
import { useGetOperations } from "~/utils/operations"

const EMPTY_FILTERS: FindProductionSitesForBrandsRequestFiltersDto = {}

type UseBuildProductionSiteFiltersOptions = {
  readonly forBrand?: boolean
  readonly defaultFilters?: FindProductionSitesForBrandsRequestFiltersDto
}

export const useBuildProductionSiteFilters = ({
  forBrand = false,
  defaultFilters = EMPTY_FILTERS,
}: UseBuildProductionSiteFiltersOptions = {}) => {
  const { filters, setFilters } = useHeaderControlsContext()
  const { user } = useAuth()
  const organizationId = user?.organization?.id ?? ""
  const { t } = useTranslation("supplier")
  const { data: productionSitesFilterOptions } =
    useFindProductionSitesFilterOptionsByOrganizationId(organizationId, {
      forBrand,
    })
  const acitivitiesTrnaslations = useGetOperations({
    translationScope: "uniqueActivitiesLabels",
  })

  const options: FilterOptions<FindProductionSitesForBrandsRequestFiltersDto>[] =
    useMemo(() => {
      const baseOptions: FilterOptions<FindProductionSitesForBrandsRequestFiltersDto>[] =
        [
          {
            label: t("productionSites.filters.country"),
            key: "country",
            type: "multiSelect",
            nothingFoundMessage: t(
              "productionSites.filters.nothingFoundMessage"
            ),
            options: Object.entries(COUNTRIES).map(([code, value]) => ({
              label: value.countryName,
              value: code,
            })),
            customRender: CountrySelectorCustomRender(COUNTRIES),
          },
          {
            label: t("productionSites.filters.performedActivities"),
            key: "performedActivities",
            type: "multiSelect",
            nothingFoundMessage: t(
              "productionSites.filters.nothingFoundMessage"
            ),
            options:
              productionSitesFilterOptions?.data.activities.map(
                (
                  activity: FindProductionSitesFilterOptionsByOrganizationIdResponseDtoActivity
                ) => ({
                  label:
                    acitivitiesTrnaslations[
                      activity.name as keyof typeof acitivitiesTrnaslations
                    ]?.name ?? activity.name,
                  value: activity.id,
                })
              ) ?? [],
          },
          {
            label: t("productionSites.filters.internalCode"),
            key: "internalCode",
            type: "multiSelect",
            nothingFoundMessage: t(
              "productionSites.filters.nothingFoundMessage"
            ),
            options:
              productionSitesFilterOptions?.data.internalCodes.map(
                (internalCode: string) => ({
                  label: internalCode,
                  value: internalCode,
                })
              ) ?? [],
          },
          {
            label: t("productionSites.filters.name"),
            key: "names",
            type: "multiSelect",
            nothingFoundMessage: t(
              "productionSites.filters.nothingFoundMessage"
            ),
            options:
              productionSitesFilterOptions?.data.names.map((name: string) => ({
                label: name,
                value: name,
              })) ?? [],
          },
        ]

      if (forBrand) {
        baseOptions.push({
          label: t("productionSites.filters.isConfidential"),
          key: "isConfidential",
          type: "select",
          nothingFoundMessage: t("productionSites.filters.nothingFoundMessage"),
          options: [
            {
              label: t("productionSites.filters.isConfidentialTrue"),
              value: true,
            },
            {
              label: t("productionSites.filters.isConfidentialFalse"),
              value: false,
            },
          ],
        })
      }

      return baseOptions
    }, [t, productionSitesFilterOptions, acitivitiesTrnaslations, forBrand])

  const filtersValue = useMemo(
    () => ({ ...defaultFilters, ...filters }),
    [defaultFilters, filters]
  )

  return {
    filtersValue,
    options,
    searchPlaceholder: t("productionSites.filters.searchPlaceholder"),
    setFiltersValue: setFilters,
    title: t("productionSites.filters.title"),
  }
}
